import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Lock, Check, Plus, Crown, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { locationsApi, formatError } from "../lib/api";

/**
 * LocationPicker — small pill button that displays the currently-selected
 * storage location ("Fridge" by default). Sits inline under inputs.
 *
 * Behaviour:
 *  - Free users: button is muted with a tiny lock icon. Tap shows a
 *    Premium upsell toast. There's only ever 1 location ("Fridge") for
 *    free tier, so the button is essentially read-only.
 *  - Premium users: button uses the brand teal. Tap opens a bottom sheet
 *    listing all household locations + an inline "+ New location" row.
 *
 * Props:
 *  - value: location_id currently selected (string|null)
 *  - onChange: (location_id: string) => void
 *  - locations: list of {id, name, is_default} (the parent fetches and passes)
 *  - onLocationsChange: () => void  — called after a new location is added
 *  - isPremium: boolean
 *  - readOnly: boolean — render as a plain non-tappable chip (no sheet, no lock)
 *  - testId: prefix for data-testid attributes (default "location-picker")
 */
export const LocationPicker = ({
  value,
  onChange,
  locations = [],
  onLocationsChange,
  isPremium = false,
  readOnly = false,
  testId = "location-picker",
}) => {
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  // Resolve the display label. Falls back to "Fridge" because every
  // household auto-creates a default Fridge on creation/join.
  const selected =
    locations.find((l) => l.id === value) ||
    locations.find((l) => l.is_default) ||
    null;
  const label = selected?.name || "Fridge";

  // Read-only mode: just a chip, no interaction at all.
  if (readOnly) {
    return (
      <span
        data-testid={`${testId}-chip`}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#F1EEE5] border border-[#E2E0D8] text-[#4F5D75]"
      >
        <MapPin className="w-3.5 h-3.5" />
        <span className="max-w-[140px] truncate">{label}</span>
      </span>
    );
  }

  const handleClick = () => {
    if (!isPremium) {
      toast.info("Upgrade to Premium to add custom storage locations", {
        description: "Free tier includes one location (Fridge).",
      });
      return;
    }
    setOpen(true);
  };

  const handlePick = (id) => {
    onChange?.(id);
    setOpen(false);
  };

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) {
      toast.error("Name the location first");
      return;
    }
    setCreating(true);
    try {
      const loc = await locationsApi.create(name);
      onLocationsChange?.();
      onChange?.(loc.id);
      setNewName("");
      setOpen(false);
      toast.success(`Added "${loc.name}"`);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        data-testid={`${testId}-btn`}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
          isPremium
            ? "bg-[#DCEDF0] border-[#277A82]/40 text-[#1F5566] hover:bg-[#C4DFE3]"
            : "bg-[#F1EEE5] border-[#E2E0D8] text-[#8A93A6] cursor-default"
        }`}
        aria-label={isPremium ? "Change storage location" : "Storage location (Premium for more)"}
      >
        <MapPin className="w-3.5 h-3.5" />
        <span className="max-w-[140px] truncate">{label}</span>
        {!isPremium && <Lock className="w-3 h-3 ml-0.5 text-[#8A93A6]" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[2147483600] bg-black/45 backdrop-blur-sm flex items-end sm:items-center justify-center"
            data-testid={`${testId}-sheet-overlay`}
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ type: "spring", stiffness: 280, damping: 28 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-[480px] bg-[#FDFBF7] rounded-t-3xl sm:rounded-3xl p-5 sm:m-4 max-h-[78vh] overflow-y-auto"
              data-testid={`${testId}-sheet`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-xl brand-gradient-bg flex items-center justify-center">
                    <MapPin className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8A93A6]">
                      Storage location
                    </div>
                    <div className="font-heading text-lg font-semibold text-[#2D3142]">
                      Where is it stored?
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  data-testid={`${testId}-close`}
                  className="w-9 h-9 rounded-full hover:bg-[#F5F3ED] flex items-center justify-center text-[#4F5D75]"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <ul className="space-y-2" data-testid={`${testId}-list`}>
                {locations.map((loc) => {
                  const active = loc.id === (selected?.id ?? null);
                  return (
                    <li key={loc.id}>
                      <button
                        type="button"
                        onClick={() => handlePick(loc.id)}
                        data-testid={`${testId}-option-${loc.id}`}
                        className={`w-full text-left tup-card p-4 flex items-center justify-between gap-3 border-2 transition-colors ${
                          active
                            ? "border-[#277A82] bg-[#DCEDF0]/50"
                            : "border-transparent hover:border-[#277A82]/40 hover:bg-[#F5F3ED]"
                        }`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                              active ? "bg-[#1F5566] text-white" : "bg-[#F1EEE5] text-[#4F5D75]"
                            }`}
                          >
                            <MapPin className="w-4 h-4" />
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold text-sm text-[#2D3142] truncate">
                              {loc.name}
                            </div>
                            {loc.is_default && (
                              <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#8A93A6]">
                                Default
                              </div>
                            )}
                          </div>
                        </div>
                        {active && <Check className="w-4 h-4 text-[#1F5566] shrink-0" />}
                      </button>
                    </li>
                  );
                })}
              </ul>

              {/* Inline "Create new location" — Premium users only get here, so
                  no extra premium-gate is needed inside the sheet. */}
              <div className="mt-5 tup-card p-4" data-testid={`${testId}-create-row`}>
                <div className="flex items-center gap-2 mb-2">
                  <Crown className="w-3.5 h-3.5 text-[#277A82]" />
                  <span className="text-[11px] uppercase tracking-[0.16em] font-bold text-[#277A82]">
                    New location
                  </span>
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleCreate();
                      }
                    }}
                    placeholder="e.g., Garage freezer"
                    data-testid={`${testId}-new-input`}
                    maxLength={40}
                    className="flex-1 bg-white border border-[#E2E0D8] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#277A82] focus:ring-1 focus:ring-[#277A82]/40"
                  />
                  <button
                    type="button"
                    onClick={handleCreate}
                    disabled={creating || !newName.trim()}
                    data-testid={`${testId}-new-submit`}
                    className="inline-flex items-center gap-1 brand-gradient-bg text-white font-semibold rounded-xl px-3 py-2 text-sm disabled:opacity-60"
                  >
                    {creating ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Plus className="w-3.5 h-3.5" />
                    )}
                    Add
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

/**
 * Hook that owns the locations list for a page. Returns:
 *  - locations: array
 *  - reload: () => void
 *  - loading: bool
 *
 * Callers shouldn't need to think about the network — just pass the list
 * to <LocationPicker> and call `reload()` from `onLocationsChange`.
 */
export const useLocations = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const list = await locationsApi.list();
      setLocations(list || []);
    } catch (err) {
      // 401 happens during auth bootstrap — silent. Other errors aren't
      // worth blocking on; the picker degrades gracefully to "Fridge".
      if (err?.response?.status && err.response.status !== 401) {
        console.warn("Failed to load locations:", err);
      }
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  return { locations, reload, loading };
};

export default LocationPicker;
